import { useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowUpRight,
  Play,
  PlugZap,
} from "lucide-react";
import {
  AgentIcon,
  announcements,
  CommunityFeaturedProjects,
  FeaturedProjectGrid,
  HomeAction,
} from "./home-page";
import { CodeBlock } from "./ui";
import { deploymentUrl } from "../../lib/deployment-url";

function Intro({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <header className="mk-destination-intro">
      <span className="mk-destination-label">{label}</span>
      <h1>{title}</h1>
      <p>{children}</p>
    </header>
  );
}

export function CommunityPage() {
  return (
    <div className="mk-destination mk-home-container mk-community-redesign">
      <h1 className="pf-visually-hidden">Community</h1>
      <section className="mk-community-socials" aria-label="Follow Proxima Forma">
        <a href="https://www.instagram.com/proximaforma/">
          <span className="mk-instagram-icon" aria-hidden="true" />
          <span>
            <strong>Follow Proxima Forma on Instagram</strong>
            <small>Community work, behind the scenes, experiments, and quick design ideas.</small>
          </span>
        </a>
        <div className="is-disabled" aria-disabled="true">
          <span className="mk-youtube-icon" aria-hidden="true"><Play size={18} fill="currentColor" /></span>
          <span>
            <strong>Proxima Forma on YouTube <em>Coming soon</em></strong>
            <small>Tutorials, feature walkthroughs, community showcases, and complete workflows.</small>
          </span>
        </div>
      </section>

      <CommunityFeaturedProjects />

      <section className="mk-community-participate" aria-labelledby="participate-title">
        <h2 id="participate-title">Ways to participate</h2>
        <div>
          <article><h3>Show your work</h3><p>Share a finished project or an experiment. Good process is as useful as polished output.</p></article>
          <article><h3>Share workflows</h3><p>Post prompts, agent setups, and the sequence that got you from an idea to a useful design.</p></article>
          <article><h3>Meet builders</h3><p>Find people working on similar products, trade feedback, and collaborate on new directions.</p></article>
        </div>
      </section>
      <p className="mk-community-submit" id="submit-project"><a href="/app">Submit your content</a> by opening a project and making it public in Share.</p>
    </div>
  );
}

export function BlogPage() {
  const olderPosts = [
    ["WORKFLOW", "Designing a product with ChatGPT without losing direction", "A practical way to keep creative intent intact while an agent does the execution.", "Sep 18"],
    ["SYSTEMS", "Why agents need design history, not only final screens", "Decisions, rejected directions, and rationale are part of the product context.", "Sep 12"],
    ["COMMUNITY", "Four projects that changed how we think about shared canvases", "A look at the work and workflows emerging from the Proxima Forma community.", "Sep 05"],
  ] as const;
  return (
    <article className="mk-destination mk-home-container mk-blog-redesign">
      <header className="mk-blog-redesign-hero">
        <span>PRODUCT · SEPTEMBER 20, 2026 · 8 MIN READ</span>
        <h1>Design files are becoming<br />interfaces for agents</h1>
        <p>What changes when a design file is no longer just a handoff artifact, but an environment that agents can read, edit, and reason about?</p>
      </header>
      <div className="mk-blog-redesign-body">
        <p>For a long time, the design file was the place where the final answer lived. Designers explored, made decisions, and eventually organized the result into screens that could be reviewed or handed to engineering.</p>
        <p>Agents change that relationship.</p>
        <p>When an agent can work directly with a canvas, the file becomes more than a collection of visual artifacts. It becomes an interface: a structured environment through which people and software can understand a product, change it, and continue the work together.</p>
        <h2>The canvas needs context</h2>
        <p>An agent can reproduce a screen from pixels, but useful design work depends on more than appearance. It needs to understand why elements exist, which patterns belong together, what has already been tried, and which decisions should remain stable.</p>
        <p>That means the useful design file contains structure, naming, reusable components, comments, references, history, and intent. These are not secondary documentation. They are part of the interface the agent uses to reason about the product.</p>
        <h2>From handoff to collaboration</h2>
        <p>Traditional design workflows tend to move in one direction: idea, design, review, implementation. Agentic workflows can be much more continuous. A person can describe an outcome, an agent can change the design, another person can review it, and the next agent can continue from the same context.</p>
        <p>The value is not simply faster production. The important shift is that the reasoning surrounding the design can stay attached to the work itself.</p>
        <h2>Designing for people and agents</h2>
        <p>This changes what a good design environment should optimize for. It still needs to be visual, expressive, and comfortable for people. But it also needs a clear underlying model that agents can inspect and manipulate reliably.</p>
        <p>The next generation of design tools will therefore be both creative environments and programmable interfaces. The canvas becomes the shared surface where human judgment and machine execution meet.</p>
      </div>
      {!!olderPosts.length && <section className="mk-blog-older" aria-labelledby="older-posts-title">
        <h2 id="older-posts-title">Older posts</h2>
        <div>
          {olderPosts.map(([category, title, description, date]) => (
            <article key={title}>
              <span>{category}</span>
              <h3>{title}</h3>
              <p>{description}</p>
              <time dateTime={`2026-09-${date.slice(-2)}`}>{date}</time>
            </article>
          ))}
        </div>
      </section>}
    </article>
  );
}

export function BlogArticle({
  slug,
}: {
  slug: "shared-design-canvas" | "bring-your-agent";
}) {
  const story = announcements.find((article) => article.slug === slug)!;
  return (
    <article className="mk-destination mk-home mk-home-container mk-blog-article">
      <a className="mk-back-link" href="/blog">
        <ArrowLeft size={16} aria-hidden="true" /> All articles
      </a>
      <Intro label={story.category} title={story.title}>
        {story.description}
      </Intro>
      <img
        className="mk-article-cover"
        src={`/marketing/${story.image}.jpg`}
        width="1312"
        height="656"
        alt={story.alt}
      />
      <div className="mk-article-body">
        {slug === "shared-design-canvas" ? (
          <>
            <h2>One place for the next version of your idea.</h2>
            <p>
              A design rarely arrives fully formed. You start with a direction,
              try a layout, spot something that could be clearer, and try again.
              Proxima Forma gives that process a shared visual space.
            </p>
            <p>
              People and connected agents work with the same frames, text,
              shapes, images, and vector paths. An agent can help build a first
              pass, while you review the result and decide where it should go
              next.
            </p>
            <h2>Keep the design open to change.</h2>
            <p>
              The canvas holds editable elements. You can ask an agent to adjust
              the hierarchy, leave a comment beside the part that needs
              attention, or open the manual editor to move a layer and refine
              its properties yourself.
            </p>
            <p>
              Organize related screens into pages so that a product flow, its
              references, and its component studies have room to develop without
              losing the wider picture.
            </p>
            <h2>Give your agent a clear starting point.</h2>
            <p>
              A good first request describes the audience, the task, and the
              feeling you want the interface to create. Add a reference when you
              already have a visual direction. Then work in small, reviewable
              steps.
            </p>
            <CodeBlock
              label="Try a first design brief"
              value="Create a calm welcome screen for a ceramics studio. Use a clear headline, a short introduction, and one primary action. Keep the text and shapes editable, and explain your layout choices."
            />
            <h2>Review together. Take the work with you.</h2>
            <p>
              Invite a reviewer to see or comment on a canvas. Use the design
              history to understand how the work evolved, and export SVG or
              structured JSON when you need to take the design into another
              workflow.
            </p>
            <p>
              Proxima Forma is a visual design workspace. Turning a design into
              a working application is a separate step for you and your
              development tools.
            </p>
            <HomeAction href="/app?auth=signup">
              Start creating <ArrowUpRight size={16} aria-hidden="true" />
            </HomeAction>
          </>
        ) : (
          <>
            <h2>Keep the conversation. Add a canvas.</h2>
            <p>
              You already have ways of working with your agent: the prompts that
              help, the references you trust, and the tools you return to.
              Proxima Forma adds a shared canvas to that workflow.
            </p>
            <p>
              A connected agent can read the saved design, inspect its elements,
              and make changes you can review. The design stays editable, so an
              iteration can be the start of the next conversation.
            </p>
            <h2>Choose your connection.</h2>
            <p>
              <a href="/connect/chatgpt">ChatGPT setup</a> walks through adding
              a connection and granting access.{" "}
              <a href="/connect/claude">The Claude guide</a> explains the
              supported Claude Code setup. If you build your own tooling, use{" "}
              <a href="/connect/agent">the custom agent guide</a> to connect
              through MCP or the REST API.
            </p>
            <p>
              Your agent’s provider account and usage remain separate from
              Proxima Forma. Choose the model and tools that fit your work.
            </p>
            <h2>Give the right context.</h2>
            <p>
              Start by asking your agent to list the canvases it can access and
              read the one you want to work on. Point it to the relevant page,
              describe the change, and ask it to preserve the parts of the
              design that already work.
            </p>
            <CodeBlock
              label="Try an iteration prompt"
              value="Read the current canvas and the linked design guidelines. Make the primary action easier to find, keep the existing copy, and explain what you changed."
            />
            <h2>Stay in control of the connection.</h2>
            <p>
              Create and manage agent access from the workspace’s Connect agent
              panel. Choose the scope appropriate for your task and revoke
              access when you no longer need it. A new connection does not
              automatically give an agent access to every private canvas.
            </p>
            <p>
              For authentication details, available tools, and integration
              examples, see the <a href="/docs/mcp">MCP guide</a> and{" "}
              <a href="/docs/api">API reference</a>.
            </p>
            <HomeAction href="/integrations">
              Connect your agent <PlugZap size={16} aria-hidden="true" />
            </HomeAction>
          </>
        )}
      </div>
    </article>
  );
}

export function ConnectionsPage() {
  return (
    <div className="mk-destination mk-home mk-home-container">
      <Intro
        label="Connect your agent"
        title="Your favorite tools. One shared canvas."
      >
        Choose how you want to work. Give your agent access to your design, then
        start shaping it together.
      </Intro>
      <div className="mk-connection-options">
        {[
          {
            agent: "openai",
            title: "ChatGPT",
            text: "Bring the canvas into your conversation. Follow the guided connection and sign-in steps.",
            href: "/connect/chatgpt",
            action: "Use with ChatGPT",
          },
          {
            agent: "claude",
            title: "Claude",
            text: "Connect Claude Code through your canvas’s agent setup instructions and MCP tools.",
            href: "/connect/claude",
            action: "Use with Claude",
          },
          {
            agent: "custom",
            title: "Custom agent",
            text: "Connect the agent stack you already use with MCP or the REST API.",
            href: "/connect/agent",
            action: "Set up your agent",
          },
        ].map((option) => (
          <article key={option.agent}>
            <AgentIcon agent={option.agent as "openai" | "claude" | "custom"} />
            <h2>{option.title}</h2>
            <p>{option.text}</p>
            <HomeAction href={option.href} kind="secondary">
              {option.action} <ArrowUpRight size={16} aria-hidden="true" />
            </HomeAction>
          </article>
        ))}
      </div>
      <aside className="mk-destination-callout">
        <div>
          <h2>Your canvas, your permissions.</h2>
          <p>
            Manage agent scope and revoke connections from the workspace. Your
            agent provider’s account and usage charges are separate.
          </p>
        </div>
        <a href="/docs/mcp#access">
          Understand agent access <ArrowUpRight size={16} aria-hidden="true" />
        </a>
      </aside>
    </div>
  );
}

export function AgentSetupPage({ claude = false }: { claude?: boolean }) {
  if (claude) {
    const steps: ConnectorStep[] = [
      {
        title: "Open Connectors",
        path: "Customize › Connectors › + › Add custom connector",
        body: "Open Claude settings and choose Add custom connector.",
      },
      {
        title: "Add Proxima Forma",
        path: "Add custom connector › Name + URL › Add",
        body: "Enter the connector name and remote MCP server URL. Configure OAuth advanced settings only if your server requires them.",
        values: [
          ["Name", "Proxima Forma"],
          ["MCP Server URL", deploymentUrl("/mcp")],
        ],
      },
      {
        title: "Connect your account",
        path: "Customize › Connectors › Proxima Forma › Connect",
        body: "Authenticate if prompted, then confirm the connector is available to your account.",
      },
      {
        title: "Enable it in chat",
        path: "New chat › + › Connectors › Proxima Forma",
        body: "Enable Proxima Forma for the conversation, then describe the product, page, or revision you want.",
      },
    ];
    return (
      <div className="mk-destination mk-home mk-home-container mk-connector-page mk-connector-page-claude">
        <ConnectorHero
          title="Design with Claude"
          description="Connect Proxima Forma as a custom connector and design directly through Claude."
        >
          <HomeAction href="#claude-flow-title">Connect with Claude</HomeAction>
          <HomeAction href="/docs/mcp" kind="secondary">Connection guide</HomeAction>
        </ConnectorHero>
        <section className="mk-connector-flow mk-connector-flow-flat" aria-labelledby="claude-flow-title">
          <div className="mk-connector-flow-heading">
            <h2 id="claude-flow-title">Connect with remote MCP</h2>
            <p>For individual accounts use Customize → Connectors. Team and Enterprise owners add the connector from Organization settings first.</p>
          </div>
          <ConnectorStepList steps={steps} />
        </section>
        <FeaturedConnectorProjects />
      </div>
    );
  }

  const clientTypes = [
    ["Desktop agent", "Remote MCP compatible"],
    ["IDE / coding agent", "Remote MCP compatible"],
    ["Internal assistant", "Remote MCP compatible"],
    ["Automation runner", "Remote MCP compatible"],
  ];
  const steps: ConnectorStep[] = [
    {
      title: "Choose your MCP client",
      body: "Open the client or agent where you want to use Proxima Forma.",
    },
    {
      title: "Add the server",
      body: "Create a remote MCP connection and paste the Proxima Forma server URL.",
      values: [["MCP Server URL", deploymentUrl("/mcp")]],
    },
    {
      title: "Authenticate",
      body: "Complete the authentication flow your client shows for the connection.",
    },
    {
      title: "Start designing",
      body: "Ask your agent to create, inspect, or revise a Proxima Forma project.",
    },
  ];
  return (
      <div className="mk-destination mk-home mk-home-container mk-connector-page mk-connector-page-custom">
      <ConnectorHero
        title="Custom connectors"
        description="Bring any MCP-compatible agent or client into Proxima Forma without changing your existing tool stack."
      >
        <HomeAction href="/docs/mcp">View MCP docs</HomeAction>
        <CopyTextButton value={deploymentUrl("/mcp")}>Copy server URL</CopyTextButton>
      </ConnectorHero>
      <section className="mk-custom-clients" aria-labelledby="custom-clients-title">
        <div>
          <h2 id="custom-clients-title">One server. Your client.</h2>
          <p>
            Use any client that can connect to a remote MCP server. Proxima
            Forma stays the shared design workspace while your agent remains
            where you already use it.
          </p>
        </div>
        <div className="mk-custom-client-grid">
          {clientTypes.map(([title, detail]) => (
            <article key={title}>
              <strong>{title}</strong>
              <span>{detail}</span>
            </article>
          ))}
        </div>
      </section>
      <section className="mk-connector-flow mk-connector-flow-flat" aria-labelledby="custom-flow-title">
        <div className="mk-connector-flow-heading">
          <h2 id="custom-flow-title">Connect in four steps</h2>
        </div>
        <ConnectorStepList steps={steps} />
      </section>
      <aside className="mk-connector-closing">
        <h2>Keep your agent. Add the design canvas.</h2>
        <p>
          The connector gives your existing agent access to Proxima Forma
          projects, editable design structure, and shared visual context.
        </p>
      </aside>
    </div>
  );
}

type ConnectorStep = {
  title: string;
  body: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  values?: ReadonlyArray<readonly [label: string, value: string]>;
};

function ConnectorHero({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <header className="mk-connector-hero">
      <h1>{title}</h1>
      <p>{description}</p>
      {children && <div className="mk-connector-hero-actions">{children}</div>}
    </header>
  );
}

function CopyTextButton({ value, children }: { value: string; children: ReactNode }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }
  return (
    <button type="button" className="mk-home-button mk-home-button-secondary" onClick={copy}>
      {copied ? "Copied" : children}
    </button>
  );
}

function ConnectorStepList({ steps }: { steps: ConnectorStep[] }) {
  return (
    <ol className="mk-connector-steps">
      {steps.map((step, index) => (
        <li key={step.title}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <div className="mk-connector-step-content">
            {step.image && (
              <img
                src={step.image}
                alt={step.imageAlt ?? ""}
                width="780"
                height="454"
              />
            )}
            <div>
            <h3>{step.title}</h3>
            {step.path && <code>{step.path}</code>}
            <p>{step.body}</p>
            {step.values && (
              <div className="mk-connector-values">
                {step.values.map(([label, value]) => <CodeBlock key={label} label={label} value={value} />)}
              </div>
            )}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function FeaturedConnectorProjects() {
  return (
    <section className="mk-connector-projects" aria-labelledby="connector-projects-title">
      <h2 id="connector-projects-title">Featured projects</h2>
      <FeaturedProjectGrid />
      <p>
        <a href="/community#submit-project">
          Submit your projects to be featured. <ArrowUpRight size={14} aria-hidden="true" />
        </a>
      </p>
    </section>
  );
}

export function ChatGPTMarketingPage() {
  const steps: ConnectorStep[] = [
    {
      title: "Enable Developer Mode",
      path: "Settings › Apps › Advanced settings › Developer mode",
      body: "Turn Developer mode on for your account. On managed workspaces, an admin may need to enable access first.",
      image: "/marketing/chatgpt-light-2026-09-20/01-developer-mode.png",
      imageAlt: "ChatGPT advanced settings with Developer mode enabled.",
    },
    {
      title: "Create the Proxima Forma app",
      path: "Settings › Apps › Create",
      body: "Use these values when creating the custom app. Authentication is configured separately in ChatGPT.",
      values: [
        ["Name", "Proxima Forma"],
        ["MCP Server URL", deploymentUrl("/mcp")],
      ],
      image: "/marketing/chatgpt-light-2026-09-20/02-create-app.png",
      imageAlt: "ChatGPT form for creating the Proxima Forma custom app.",
    },
    {
      title: "Scan and review tools",
      path: "Create app › Scan Tools › review actions › Create",
      body: "Let ChatGPT discover the MCP tools. Complete authorization if prompted, review the available actions, then create the app.",
      image: "/marketing/chatgpt-light-2026-09-20/03-review-tools.png",
      imageAlt: "ChatGPT showing the actions exposed by Proxima Forma.",
    },
    {
      title: "Use Proxima Forma in chat",
      path: "New chat › Apps › Proxima Forma",
      body: "Select the app for the message, then describe what you want to create or revise.",
      values: [["App mention", "@Proxima Forma"]],
      image: "/marketing/chatgpt-light-2026-09-20/04-select-app.png",
      imageAlt: "ChatGPT app picker with Proxima Forma selected.",
    },
  ];
  return (
    <div className="mk-destination mk-home mk-home-container mk-connector-page mk-connector-page-chatgpt">
      <ConnectorHero
        title="Design with ChatGPT"
        description="Describe what you want. ChatGPT turns it into a Proxima Forma project."
      >
          <span className="mk-chatgpt-plugin">
            <span className="mk-chatgpt-badge">
              <AgentIcon agent="openai" />
              <strong>Official ChatGPT plugin</strong>
            </span>
            <span className="mk-chatgpt-pending">Pending approval</span>
          </span>
          <HomeAction href="#chatgpt-flow-title" kind="secondary">Try it now in developer mode</HomeAction>
      </ConnectorHero>
      <section className="mk-connector-flow mk-connector-flow-chatgpt" aria-labelledby="chatgpt-flow-title">
        <div className="mk-connector-flow-heading">
          <h2 id="chatgpt-flow-title">Try it now with Developer Mode + MCP</h2>
        </div>
        <ConnectorStepList steps={steps} />
      </section>
      <FeaturedConnectorProjects />
    </div>
  );
}
